import React, { useEffect, useState } from "react";
import styles from './UpdateDocumentModal.module.scss';
import Modal from "../modal/Modal";
import Button from "../button/Button";
import Dropdown from "../dropdown/Dropdown";
import CustomDatePicker from "../customDatePicker/CustomDatePicker";
import axiosClient from "../../api/axiosClient";
import LoadingOverlay from "../loadingOverlay/LoadingOverlay";
import { useToasts } from "../../contexts/ToastContext";
import Icon from "../icon/Icon";
import PropTypes from "prop-types";

const UpdateDocumentModal = ({ isOpen, onClose, document, onUpdateSuccess, endpointPrefix = 'clinical-sites' }) => {
  const [type, setType] = useState('Normal');
  const [activationDate, setActivationDate] = useState(null);
  const [expirationDate, setExpirationDate] = useState(null);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { showToast } = useToasts();

  useEffect(() => {
    if (document && isOpen) {
      setType(document.isAgreement ? 'Agreement' : 'Normal');
      setActivationDate(document.startDate ? new Date(document.startDate) : null);
      setExpirationDate(document.endDate ? new Date(document.endDate) : null);
      setErrors({});
    }
  }, [document, isOpen]);

  const validate = () => {
    const newErrors = {};
    if (type === 'Agreement') {
      if (!activationDate) newErrors.activationDate = 'Activation Date is required';
      if (!expirationDate) newErrors.expirationDate = 'Expiration Date is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setIsLoading(true);

    const payload = {
      isAgreement: type === 'Agreement',
      startDate: activationDate ? activationDate.toISOString().split('T')[0] : null,
      endDate: expirationDate ? expirationDate.toISOString().split('T')[0] : null,
    };

    try {
      await axiosClient.patch(`${endpointPrefix}/documents/${document.id}`, payload);
      showToast('Document updated successfully');
      onUpdateSuccess();
      onClose();
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleteLoading(true);
    try {
      await axiosClient.delete(`${endpointPrefix}/documents/${document.id}`);
      showToast('Document deleted successfully');
      onUpdateSuccess();
      onClose(); // Close main modal too
      setShowDeleteModal(false);
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setIsDeleteLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <Modal onClose={onClose} scrollable={false}>
        <div className={styles.form}>
          {isLoading && <LoadingOverlay />}
          <h3>Update Document</h3>

          {document && (
            <div className={styles.documentRow}>
              <div className={styles.fileInfo}>
                <div className={styles.iconWrapper}>
                  <Icon name="file-text" />
                </div>
                <span className={styles.documentName}>{document.fileName}</span>
              </div>
              <button 
                 onClick={() => setShowDeleteModal(true)}
                 title="Delete Document"
                 disabled={isLoading}
               >
                 <Icon name="trash" />
               </button>
            </div>
          )}
          <div className={styles.fields}>
            <Dropdown 
              label="Document Type"
              options={[
                { label: 'Agreement', value: 'Agreement' },
                { label: 'Normal', value: 'Normal' }
              ]}
              value={type}
              onChange={(val) => {
                setType(val);
                setErrors(prev => ({ ...prev, type: '' }));
              }}
            />

            {type === 'Agreement' && (
              <div className={styles.dateRow}>
                <CustomDatePicker 
                  label="Activation Date"
                  date={activationDate}
                  onChange={(date) => {
                    setActivationDate(date);
                    setErrors(prev => ({ ...prev, activationDate: '' }));
                    if (expirationDate && date && expirationDate < date) {
                      setExpirationDate(null);
                    }
                  }}
                  showIcon
                  maxDate={expirationDate}
                  error={errors.activationDate}
                />
                <CustomDatePicker 
                  label="Expiration Date"
                  date={expirationDate}
                  onChange={(date) => {
                    setExpirationDate(date);
                    setErrors(prev => ({ ...prev, expirationDate: '' }));
                    if (activationDate && date && activationDate > date) {
                      setActivationDate(null);
                    }
                  }}
                  showIcon
                  minDate={activationDate}
                  error={errors.expirationDate}
                />
              </div>
            )}
          </div>
          <div className={styles.actions}>
            <Button
              label="Cancel"
              variant="white"
              size="medium"
              onClick={onClose}
            />
            <Button
              label="Update"
              variant="primary"
              size="medium"
              onClick={handleSubmit}
            />
          </div>
        </div>
      </Modal>

      {showDeleteModal && (
        <Modal>
          <div className={styles.deleteModal}>
            {isDeleteLoading && <LoadingOverlay />}
            <h2>Are you sure you want to delete this document?</h2>
            <div className={styles.buttons}>
              <Button
                label="Delete"
                size="small"
                variant="secondary"
                onClick={handleDelete}
              />
              <Button
                label="Cancel"
                size="small"
                variant="secondary"
                onClick={() => setShowDeleteModal(false)}
              />
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

UpdateDocumentModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  document: PropTypes.object,
  onUpdateSuccess: PropTypes.func.isRequired,
  endpointPrefix: PropTypes.string
};

export default UpdateDocumentModal;
